import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { ChevronRight, Compass, MapPin } from 'lucide-react';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { useGeoLocation } from '../../hooks/useGeoLocation';
import { useNearbyRestaurants } from '../../hooks/useNearbyRestaurants';
import type { FeedRestaurant } from '../../types/feed';
import './NearbyStrip.css';

interface Props {
  onOpenFeed: () => void;
  onOpenRestaurant: (slug: string) => void;
  venueId?: string | null;
  venueName?: string | null;
}

export function NearbyStrip({ onOpenFeed, onOpenRestaurant, venueId, venueName }: Props) {
  const { t } = useTranslation();
  const { token } = useCustomerAuth();
  const { coords, status } = useGeoLocation(true);
  const geoReady = status === 'granted' || status === 'denied' || status === 'timeout' || status === 'unavailable';

  const { items, loading } = useNearbyRestaurants({
    lat: coords?.lat,
    lng: coords?.lng,
    token,
    limit: 12,
    enabled: geoReady,
    venueId,
  });

  return (
    <motion.section
      className="ns-wrap"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      aria-label={t('home.nearbyHeading', { defaultValue: 'Рестораны рядом' })}
    >
      <header className="ns-head">
        <div className="ns-head-text">
          <div className="ns-eyebrow">
            <MapPin size={12} strokeWidth={2.4} />
            {t('home.nearbyEyebrow', { defaultValue: 'Поблизости' })}
          </div>
          <h2 className="ns-title">
            {venueName
              ? t('home.nearbyInVenue', { venue: venueName, defaultValue: 'Рестораны из {{venue}}' })
              : t('home.nearbyHeading', { defaultValue: 'Рестораны рядом' })}
          </h2>
        </div>
        <button type="button" className="ns-all-btn" onClick={onOpenFeed}>
          {t('home.nearbyAll', { defaultValue: 'Все' })}
          <ChevronRight size={16} strokeWidth={2.2} />
        </button>
      </header>

      <div className="ns-scroller" role="list">
        {loading && items.length === 0 && (
          <>
            {[0, 1, 2].map((i) => (
              <div key={i} className="ns-card ns-card--skel" aria-hidden role="listitem">
                <div className="ns-shimmer" />
              </div>
            ))}
          </>
        )}

        {!loading && items.length === 0 && (
          <button type="button" className="ns-empty" onClick={onOpenFeed}>
            <Compass size={20} strokeWidth={1.75} />
            <span>{t('home.nearbyEmpty', { defaultValue: 'Открыть весь каталог' })}</span>
          </button>
        )}

        {items.map((r, i) => (
          <NearbyCard key={r.id} r={r} index={i} onOpen={() => onOpenRestaurant(r.slug)} />
        ))}

        {items.length > 0 && (
          <button
            type="button"
            className="ns-more"
            onClick={onOpenFeed}
            aria-label={t('home.nearbyAll', { defaultValue: 'Все' })}
          >
            <ChevronRight size={22} strokeWidth={2.4} />
            <span>{t('home.nearbyAll', { defaultValue: 'Все' })}</span>
          </button>
        )}
      </div>
    </motion.section>
  );
}

function NearbyCard({ r, index, onOpen }: { r: FeedRestaurant; index: number; onOpen: () => void }) {
  const { t } = useTranslation();
  const distance =
    r.distanceKm == null
      ? null
      : r.distanceKm < 1
        ? t('feed.distanceM', { n: Math.round(r.distanceKm * 1000) })
        : t('feed.distanceKm', { n: r.distanceKm.toFixed(1) });

  return (
    <motion.button
      type="button"
      className={`ns-card${r.openNow ? '' : ' ns-card--closed'}`}
      onClick={onOpen}
      role="listitem"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.04 * index, duration: 0.32, ease: 'easeOut' }}
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.97 }}
      aria-label={r.name}
    >
      <div className="ns-card-cover">
        {r.coverImageUrl ? (
          <img src={r.coverImageUrl} alt="" className="ns-card-img" />
        ) : (
          <div className="ns-card-fallback" aria-hidden>
            <span>{r.name.slice(0, 2).toUpperCase()}</span>
          </div>
        )}
        <div className="ns-card-grad" />
        {r.openNow && <span className="ns-dot" aria-hidden />}
      </div>
      <div className="ns-card-body">
        <div className="ns-card-name">{r.name}</div>
        <div className="ns-card-meta">
          {r.openNow ? (
            <span className="ns-meta-open">{t('feed.open')}</span>
          ) : (
            <span className="ns-meta-closed">{t('feed.closed')}</span>
          )}
          {distance && <span className="ns-meta-dot" aria-hidden>·</span>}
          {distance && <span className="ns-meta-dist">{distance}</span>}
        </div>
      </div>
    </motion.button>
  );
}
